// src/pages/CheckoutPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import api from "../api";
import { ArrowLeft, Home, ChevronDown } from "lucide-react";
import { getOrCreateSessionId } from "../utils/session";

export default function CheckoutPage() {
  const { productId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const selectedCustomization = useMemo(() => state?.customization || {}, [state?.customization]);
  const quantity = state?.quantity || 1;


  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("Mumbai");
  const [pincode, setPincode] = useState("");
  const [shippingCharges, setShippingCharges] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  // Initialize from location.state if available
  const initialProduct = state?.product || null;
  const [product, setProduct] = useState(initialProduct);
  const [selectedColor, setSelectedColor] = useState(
  initialProduct
    ? selectedCustomization?.Color || initialProduct.color?.[0] || ""
    : ""
);
  const slide_index = state?.slide_index || 0;
  const sessionId = getOrCreateSessionId();




  const allowedCities = {
    Mumbai: ["400001","400005","400006","400007","400008","400050","400051","400058","400061","400088","400049","400033","400002"],
    Navi_Mumbai: ["400701","400703","400708","721428","721463","400709","400710","400614","410210","410206"],
    Thane: ["400601","400607","400605","400603","421204","421302","400607"],
  };

  const sanitizeText = (str) => str.replace(/[<>;"'`]/g, "").trim();

  const validateInputs = () => {
    const cleanName = sanitizeText(name);
    if (!cleanName || cleanName.length < 2) return { valid: false, message: "Please enter your full name (at least 2 characters)." };
    if (!/^[a-zA-Z\s.'-]+$/.test(cleanName)) return { valid: false, message: "Name can only contain letters, spaces, hyphens, apostrophes, and dots." };

    const trimmedEmail = sanitizeText(email).toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail)) return { valid: false, message: "Please enter a valid email address." };

    let normalizedPhone = phone.replace(/\D/g, "");
    if (normalizedPhone.startsWith("91") && normalizedPhone.length === 12) normalizedPhone = normalizedPhone.slice(2);
    else if (normalizedPhone.startsWith("0") && normalizedPhone.length === 11) normalizedPhone = normalizedPhone.slice(1);
    if (!/^\d{10}$/.test(normalizedPhone)) return { valid: false, message: "Please enter a valid 10-digit mobile number." };

    const cleanAddress = sanitizeText(address);
    if (!cleanAddress || cleanAddress.length < 3) return { valid: false, message: "Please enter a valid address." };
    if (!allowedCities[city] || !allowedCities[city].includes(pincode)) return { valid: false, message: "We only deliver to selected pincodes in Mumbai, Navi Mumbai, and Thane." };

    return { valid: true, name: cleanName, email: trimmedEmail, phone: normalizedPhone, address: cleanAddress };
  };

  // --- Fetch product with session support ---
// Fetch product only if we don't already have it
useEffect(() => {
  if (product) return; // skip fetch if product already exists

  const fetchProduct = async () => {
    try {
      const sessionId = getOrCreateSessionId();
      const res = await api.get(`/cproduct/${productId}`, { params: { session_id: sessionId } });
      const prod = res.data;
      setProduct(prod);

      const colorFromSelection = selectedCustomization?.Color || prod.color?.[0] || "";
      setSelectedColor(colorFromSelection);

    } catch (err) {
      console.error("Failed to fetch product:", err);
      setError("Product not found or failed to load.");
    }
  };
  fetchProduct();
}, [productId, product, selectedCustomization]);

  // --- Shipping charges ---
  useEffect(() => {
    let charge = 0;
    if (city === "Mumbai") charge = 40;
    else if (city === "Navi_Mumbai") charge = 60;
    else if (city === "Thane") charge = 50;
    setShippingCharges(charge);
  }, [city]);

  // --- Image helpers ---
  const getImagesForColor = (imageMap, color) => {
    if (!imageMap || !color) return [];
    if (Array.isArray(imageMap)) {
      const entry = imageMap.find((e) => e.color?.toLowerCase() === color.toLowerCase());
      return entry?.urls || [];
    }
    if (typeof imageMap === "object") return imageMap[color.toLowerCase()] || [];
    return [];
  };

  const getMainImage = () => {
    const images = getImagesForColor(product?.imageUrl, selectedColor);
    return images[0] || product?.imageUrls?.[0] || "";
  };

  const getFirstOption = (label) => {
    const field = product?.customization_schema?.find(f => f.label.toLowerCase() === label.toLowerCase());
    if (!field || !field.options?.length) return "";
    return typeof field.options[0] === "string" ? field.options[0] : field.options[0].label;
  };

  const getDisplayPrice = useCallback(() => {
    if (!product) return 0;
    if (Array.isArray(product.price)) {
      const sizeField = product.customization_schema?.find(f => f.label.toLowerCase() === "size");
      if (sizeField) {
        const selectedSize = selectedCustomization?.Size || getFirstOption("Size");
        if (selectedSize) {
          const sizeIndex = sizeField.options.findIndex(opt => (typeof opt === "string" ? opt : opt.label).toLowerCase() === selectedSize.toLowerCase());
          if (sizeIndex >= 0 && sizeIndex < product.price.length) return product.price[sizeIndex];
        }
      }
      return product.price[0];
    }
    return product.price;
  }, [product, selectedCustomization]);

  useEffect(() => {
    const productPrice = getDisplayPrice();
    if (productPrice != null) setTotal(productPrice * quantity + shippingCharges);
  }, [shippingCharges, product, quantity, getDisplayPrice]);

  const handleCheckout = async () => {
    setLoading(true);
    setError("");

    const validation = validateInputs();
    if (!validation.valid) {
      setError(validation.message);
      setLoading(false);
      return;
    }

    const cleanName = validation.name;
    const cleanEmail = validation.email;
    const cleanPhone = validation.phone;

    try {
      const customizationToSend = Object.keys(selectedCustomization).length
        ? selectedCustomization
        : { Color: getFirstOption("Color"), Size: getFirstOption("Size"), Material: getFirstOption("Material") };

      const response = await api.post(`/create-razorpay-order`, {
        product_id: productId,
        name: cleanName,
        email: cleanEmail,
        phone: cleanPhone,
        address,
        city,
        pincode,
        customization: customizationToSend,
        quantity,
        slide_index,   // make sure this exists
        session_id: sessionId,
        product_desc: product?.description || ""  // ✅ fix for undefined
      });

      const { razorpay_order_id, razorpay_key, amount, currency } = response.data;
      const options = {
        key: razorpay_key,
        amount,
        currency,
        order_id: razorpay_order_id,
        name: "A2XShop",
        description: "Test Transaction",
        handler: function (response) {
          console.log("Payment success:", response);
          navigate("/", { state: { paymentResponse: response } });
        },
        prefill: { name: cleanName, email: cleanEmail, contact: cleanPhone },
        notes: { address },
        theme: { color: "#3399cc" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          const fieldErrors = {};
          detail.forEach(d => { const field = d.loc[d.loc.length-1]; fieldErrors[field] = d.msg; });
          setErrors(fieldErrors);
        } else if (typeof detail === "string") setErrors({ general: detail });
        else setErrors({ general: "Something went wrong. Please try again." });
      } else setErrors({ general: "Server error. Please try again later." });
    } finally {
      setLoading(false);
    }
  };

  const showStandardOptions = Object.keys(selectedCustomization).length === 0;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Back + Home */}
      <div className="relative flex items-center mb-4">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-black">
          <ArrowLeft className="w-5 h-5 mr-2" />
        </button>
        <button onClick={() => navigate("/")} className="absolute left-1/2 transform -translate-x-1/2 text-gray-600 hover:text-black">
          <Home className="w-5 h-5" />
        </button>
      </div>

      {product && (
        <div className="w-full aspect-[2/3] md:min-h-[350px] overflow-hidden rounded-lg mb-4">
          <img src={getMainImage()} alt={product.name} className="w-full h-full object-cover" />
        </div>
      )}

      <p className="text-gray-700 mb-2">{product?.description}</p>

      {/* Standard Options */}
      {showStandardOptions && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold mb-2">Product Options</h2>
          <table className="w-full border border-gray-300 text-sm rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left px-3 py-2 border-b border-gray-300">Option</th>
                <th className="text-left px-3 py-2 border-b border-gray-300">Selected</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="px-3 py-2 font-medium">Color</td>
                <td className="px-3 py-2">{getFirstOption("Color")}</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="px-3 py-2 font-medium">Size</td>
                <td className="px-3 py-2">{getFirstOption("Size")}</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="px-3 py-2 font-medium">Material</td>
                <td className="px-3 py-2">{getFirstOption("Material")}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Price Summary */}
      <div className="border-t border-b py-4 mt-4 mb-4">
        <div className="flex justify-between mb-2">
          <span className="text-gray-700">Price ({quantity} {quantity>1?"items":"item"})</span>
          <span className="text-gray-900">₹{getDisplayPrice()*quantity}</span>
        </div>
        <div className="flex justify-between mb-2">
          <span className="text-gray-700">Shipping</span>
          <span className="text-gray-900">₹{shippingCharges}</span>
        </div>
        <div className="flex justify-between font-bold text-lg border-t pt-2">
          <span>Total</span>
          <span>₹{total}</span>
        </div>
      </div>

      {/* Customization Summary */}
      {Object.keys(selectedCustomization).length>0 && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold mb-2">Customization Summary</h2>
          <table className="w-full border border-gray-300 text-sm rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left px-3 py-2 border-b border-gray-300">Option</th>
                <th className="text-left px-3 py-2 border-b border-gray-300">Selected</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(selectedCustomization).map(([key,value]) => (
                <tr key={key} className="border-b border-gray-200">
                  <td className="px-3 py-2 font-medium">{key}</td>
                  <td className="px-3 py-2">{Array.isArray(value)?value.join(", "):value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Contact Form */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Full Name</label>
        <input type="text" className="w-full border p-2 rounded" value={name} onChange={e=>setName(e.target.value)} required/>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Address</label>
        <input type="text" className="w-full border p-2 rounded" value={address} onChange={e=>setAddress(e.target.value)} required/>
      </div>
      <div className="mb-4 relative">
        <label className="block text-sm font-medium mb-1">City</label>
        <div className="relative">
          <select className="w-full border rounded-md pl-3 pr-10 py-2 text-sm appearance-none bg-white" value={city} onChange={e=>setCity(e.target.value)}>
            {Object.keys(allowedCities).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500"/>
        </div>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Pincode</label>
        <div className="relative">
          <select className="w-full border rounded-md pl-3 pr-10 py-2 text-sm appearance-none bg-white" value={pincode} onChange={e=>setPincode(e.target.value)}>
            <option value="">Select Pincode</option>
            {allowedCities[city]?.map(pc => <option key={pc} value={pc}>{pc}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500"/>
        </div>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Email</label>
        <input type="email" className="w-full border p-2 rounded" value={email} onChange={e=>setEmail(e.target.value)} required/>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Phone</label>
        <input type="tel" className="w-full border p-2 rounded" value={phone} onChange={e=>setPhone(e.target.value)} required/>
      </div>

      {error && <p className="text-red-500 text-sm mb-2">{error}</p>}

      <button onClick={handleCheckout} className="px-6 py-2 bg-green-600 text-white rounded-full mt-4 disabled:opacity-50" disabled={loading}>
        {loading ? "Loading Razorpay..." : "Pay Now"}
      </button>
    </div>
  );
}
